import React, { useCallback, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import { IconButton, Paper, styled, Collapse, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Menu, MenuItem, Chip, MenuList, Typography, Button } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { format, formatRelative, max, min } from 'date-fns';
import useAnimationFrame from '../../hooks/useAnimationFrame';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import type { GeoJsonFeature } from '../../utils/ogc';
import useThrottledValue from '../../hooks/useThrottledValue';
import TimeScrubberControls from './TimeScrubberControls';
import TimePresetMenuAndButton, { TimePresetMenu, TimePresetMenuAndIcon } from '../menu/TimePresetMenuAndButton';
import { WelcomeDialogAndButton } from '../menu/WelcomeDialog';





const FlashingDot = styled('div')(({ theme }) => ({
  width: 5,
  height: 5,
  display: 'inline-block',
  // mx: 0.5,
  borderRadius: '50%',
  backgroundColor: 'text.secondary',
  '&.live': {
    backgroundColor: theme.palette.error.main,
    // boxShadow: `0 0 6px ${theme.palette.error.main}, 0 0 14px ${theme.palette.error.main}55`,
    animation: 'flashing 4s infinite',
    '@keyframes flashing': {
      '0%': { opacity: 1, backgroundColor: theme.palette.error.main, boxShadow: `0 0 6px ${theme.palette.error.main}`, },
      '50%': { opacity: 0.8, backgroundColor: 'black', boxShadow: `0 0 0px ${theme.palette.error.main}`, },
      '100%': { opacity: 1, backgroundColor: theme.palette.error.main, boxShadow: `0 0 6px ${theme.palette.error.main}`, },
    },
  },
  
  // boxShadow: `0 0 6px ${theme.palette.error.main}, 0 0 14px ${theme.palette.error.main}55`,
  // border: `1px solid ${theme.palette.error.main || theme.palette.error.main}`,
}));


const LiveIndicator = React.memo(({ currentTime, timeRange, onTimeChange }: { currentTime: Date | undefined; timeRange: [Date, Date, boolean]; onTimeChange: (time: Date) => void }) => {
  const [fromTime, toTime, live] = timeRange;
  const liveCursor = live && (currentTime == toTime);
  const goToLive = useCallback(() => onTimeChange(toTime), [onTimeChange, toTime]);
  return (
    live && (currentTime != null) && <Tooltip title={liveCursor ? 'Map is showing the latest data' : 'View latest data'} placement='top'>
      <Chip size='small' sx={{ height: '1.1em' }} onClick={liveCursor ? undefined : goToLive} label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FlashingDot className={liveCursor ? 'live' : ''} />
        {liveCursor ? 'live' : 'go to now'}
      </Box>} />
    </Tooltip>
  )
})


const TimeLabel = React.memo(({ currentTime, timeRange, onTimeChange, setTimeRange }: { currentTime: Date | undefined; timeRange: [Date, Date, boolean]; onTimeChange: (time: Date) => void; setTimeRange: (range: [Date, Date]) => void }) => {
  const [fromTime, toTime, live] = timeRange;
  const liveCursor = live && (currentTime == toTime);
  const throttledCurrentTime1s = useThrottledValue(currentTime, 1000);
  // const throttledCurrentTimeFast = useThrottledValue(currentTime, 100);
  const t = throttledCurrentTime1s;
  // console.log('TimeLabel render', { t, liveCursor, live, fromTime, toTime });
  const formattedTime = useMemo(() => {
    return (
      // !t ? '' :
      liveCursor && t ? format(new Date(t), 'h:mm aaa') : 
      live ? formatRelative(new Date(t || toTime), new Date()) : 
      t ? format(new Date(t), 'eeee, MMMM d, yyyy hh:mm aaa') :   
      fromTime && toTime ? <>
        <span>{`${format(new Date(fromTime), 'PP p')}`}</span>
        <span>-</span>
        <span>{`${format(new Date(toTime), 'PP p')}`}</span>
      </> 
      : ''
    );
  }, [liveCursor, live, t, fromTime, toTime]);
  const tooltipDescription = useMemo(() => {
    if (!t) return 'Showing peak depth across time range';
    if (liveCursor) return `Showing live data as of ${format(new Date(t), 'PP p')}`;
    if (live) return `Showing data as it was - ${formatRelative(new Date(t), new Date())}`;
    if (t) return `Current time (use slider to change)`;
    return '';
  }, [liveCursor, live, t]);
  return (
    <Paper elevation={0} sx={{ 
        position: 'absolute', top: -1, left: '50%', 
        transform: 'translate(-50%, -100%)', 
        // zIndex: -1, 
        fontSize: '0.70rem', fontWeight: '500', 
        px: 1, py: 0.3, 
        boxShadow: 0, 
        borderRadius: '12px 12px 0 0',
        maxWidth: '100%', 
        textWrapMode: 'nowrap', 
        // bgcolor: 'background.opaque',
        display: 'flex', alignItems: 'center', gap: 1,
        // width: 'calc(100% - 2em)',
        justifyContent: 'center',
      }}>
        <WelcomeDialogAndButton />
        <LiveIndicator currentTime={throttledCurrentTime1s} timeRange={timeRange} onTimeChange={onTimeChange} />
        <Tooltip title={tooltipDescription} placement='top'>
          <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {formattedTime}
          </Box>
        </Tooltip>
        {setTimeRange && <TimePresetMenuAndIcon timeRange={timeRange} setTimeRange={setTimeRange} noButton />}
    </Paper>
  )
});

// const TimePresetMenuAndButton = React.memo(({ timeRange, setTimeRange }: { timeRange: [Date, Date, boolean]; setTimeRange: (range: [Date, Date]) => void }) => {
//   const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  
//   const openMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)
//   const closeMenu = () => setAnchorEl(null)
//   return (<>
  
//     <HistoryIcon onClick={openMenu} />
//     <TimePresetMenu 
//       anchorEl={anchorEl}
//       onClose={closeMenu}
//       timeRange={timeRange}
//       setTimeRange={setTimeRange}
//     />
//   </>
//   )
// })

export default TimeLabel;